import { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Radio,
  Space,
  Switch,
  InputNumber,
  Button,
  Spin,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { openNotification } from 'util/helpers';
import {
  useFindUniqueQuestQuery,
  useUpdateQuestMutation,
} from '@src/components/generated/graphql';
import { useSession } from 'next-auth/react';
import { CollectableInput } from './CollectableInput';
import styles from './quest-settings.module.css';

const notify = (type, msg, desc) => {
  openNotification(msg, desc, 'top', 2, type);
};

const QuestSettings = ({ questId, sceneId }) => {
  const { data: session } = useSession();
  const [updateQuest] = useUpdateQuestMutation();
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [disableBtn, setDisableBtn] = useState(true);
  const [hasReward, setHasReward] = useState(false);
  const [hasDefaultModal, setHasDefaultModal] = useState(false);
  const [correctItems, setCorrectItems] = useState([] as any);
  const [fakeItems, setFakeItems] = useState([] as any);
  const [i, setI] = useState(1);
  const [j, setJ] = useState(1);

  const { data, loading, error } = useFindUniqueQuestQuery({
    variables: {
      where: {
        questId: questId ?? '',
      },
    },
    fetchPolicy: 'no-cache',
  });

  function deleteItem(key: string, type: 'correct' | 'fake') {
    if (type === 'correct') {
      setCorrectItems((prev) => prev.filter((e) => e.props.identifier !== key));
    } else {
      setFakeItems((prev) => prev.filter((e) => e.props.identifier !== key));
    }

    setDisableBtn(false);
  }

  async function onFinish(values) {
    setLoadingUpdate(true);

    const collectables = correctItems.map((e) => {
      const id = e.props.identifier;
      const el = document.getElementById(id) as any;
      return el.value.toString();
    });

    const fake = fakeItems.map((e) => {
      const id = e.props.identifier;
      const el = document.getElementById(id) as any;
      return el.value.toString();
    });

    const res = await updateQuest({
      variables: {
        data: {
          sceneId,
          name: values.name,
          startPrompt: values.startPrompt,
          splashScreenMessage: values.splashScreenMessage,
          enableDefaultModal: hasDefaultModal,
          finishPrompt: values.finishPrompt ?? undefined,
          hasReward,
          rewardAmount: values.rewardAmount,
          rewardType: values.rewardType,
          collectableItem: collectables,
          fakeItem: fake,
        },
      },
    });

    setLoadingUpdate(false);

    if (res?.data?.updateQuest.status === 'success') {
      notify('success', 'Successfully', 'saved changes');
      setDisableBtn(true);
    } else {
      notify('error', 'Error', 'something went wrong please try again');
    }
  }

  const quest = data?.findUniqueQuest.quest;

  function enableSave() {
    if (disableBtn) {
      setDisableBtn(false);
    }
  }

  useEffect(() => {
    if (error || loading) {
      return;
    }

    if (quest?.hasReward) {
      setHasReward(true);
    }

    if (quest?.collectableItem.length) {
      const arr = quest.collectableItem.map((value, i) => {
        return (
          <CollectableInput
            key={i.toString()}
            identifier={`${i.toString()}-correct`}
            deleteItem={deleteItem}
            type={'correct'}
            userEmail={session?.user.email}
            enableSave={enableSave}
            defaultValue={value as any}
          />
        );
      });
      setI(arr.length);
      setCorrectItems(arr);
    } else {
      if (!quest) {
        return;
      }
      setCorrectItems((p) => [
        ...p,
        <CollectableInput
          key={'0'}
          identifier={'0-correct'}
          deleteItem={deleteItem}
          type={'correct'}
          userEmail={session?.user.email}
          enableSave={enableSave}
        />,
      ]);
    }

    if (quest?.fakeItem.length) {
      const arr = quest.fakeItem.map((value, i) => {
        return (
          <CollectableInput
            key={i.toString()}
            identifier={`${i.toString()}-fake`}
            deleteItem={deleteItem}
            type={'fake'}
            userEmail={session?.user.email}
            enableSave={enableSave}
            defaultValue={value as any}
          />
        );
      });
      setJ(arr.length);
      setFakeItems(arr);
    } else {
      if (!quest) {
        return;
      }
      setFakeItems((p) => [
        ...p,
        <CollectableInput
          key={'0'}
          identifier={'0-fake'}
          deleteItem={deleteItem}
          type={'fake'}
          userEmail={session?.user.email}
          enableSave={enableSave}
        />,
      ]);
    }
  }, [quest]);

  if (loading || error) {
    return (
      <Spin
        style={{ marginLeft: '45%', marginTop: 40 }}
        indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />}
      ></Spin>
    );
  }

  return (
    <div className={styles.formContainer}>
      <Form
        layout='vertical'
        onFinish={onFinish}
        onValuesChange={() => setDisableBtn(false)}
      >
        <p className={styles.title}>Copy</p>
        <Form.Item
          label='Name'
          name='name'
          initialValue={quest?.name}
          rules={[{ required: true, message: 'Please enter name' }]}
        >
          <Input placeholder='Enter name'></Input>
        </Form.Item>
        <Form.Item
          label='Splash screen message'
          name='splashScreenMessage'
          initialValue={quest?.splashScreenMessage}
          rules={[
            { required: true, message: 'Please enter splash screen message' },
          ]}
        >
          <Input.TextArea
            style={{ height: 125, resize: 'none' }}
            placeholder='Enter message'
          ></Input.TextArea>
        </Form.Item>
        <Form.Item
          label='Start Prompt'
          name='startPrompt'
          initialValue={quest?.unMarkedStartPrompt}
          rules={[{ required: true, message: 'Please enter start message' }]}
        >
          <Input.TextArea
            style={{ height: 125, resize: 'none' }}
            placeholder='Enter message'
          ></Input.TextArea>
        </Form.Item>
        <Form.Item
          label='Finish Prompt'
          name='finishPrompt'
          initialValue={quest?.finishPrompt ?? undefined}
        >
          <Input.TextArea
            style={{ height: 125, resize: 'none' }}
            placeholder='Enter message'
          ></Input.TextArea>
        </Form.Item>
        <p className={styles.title}>Reward</p>
        <Form.Item label='Enable default modal'>
          <Switch
            onChange={(v) => {
              setHasDefaultModal(v);
              setDisableBtn(false);
            }}
            defaultChecked={quest?.enableDefaultModal ?? false}
          ></Switch>
        </Form.Item>
        <Form.Item label='Has reward'>
          <Switch
            onChange={(v) => {
              setHasReward(v);
              setDisableBtn(false);
            }}
            defaultChecked={quest?.hasReward ?? false}
          ></Switch>
        </Form.Item>
        {hasReward && (
          <>
            <Form.Item
              label='Reward type'
              name='rewardType'
              initialValue={'TOKEN'}
            >
              <Radio.Group>
                <Space direction='vertical'>
                  <Radio value={'TOKEN'}>OzoneToken</Radio>
                  <Radio disabled value={'NFT'}>
                    NFT
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              label='Reward amount'
              name='rewardAmount'
              initialValue={quest?.rewardAmount ?? undefined}
              rules={[
                { required: true, message: 'Please enter reward amount' },
              ]}
            >
              <InputNumber
                type='number'
                min={1}
                max={2500}
                precision={2}
              ></InputNumber>
            </Form.Item>
          </>
        )}
        <p className={styles.title}>Collectables</p>
        <p>Correct items</p>
        <div className={styles.wrapperItems}>
          <div className={styles.items}>{correctItems.map((e) => e)}</div>
          <Button
            type='dashed'
            className={styles.addButton}
            onClick={() => {
              setI((p) => p + 1);
              setDisableBtn(false);
              setCorrectItems((p) => [
                ...p,
                <CollectableInput
                  key={i.toString()}
                  identifier={`${i.toString()}-correct`}
                  deleteItem={deleteItem}
                  type={'correct'}
                  userEmail={session?.user.email}
                  enableSave={enableSave}
                />,
              ]);
            }}
          >
            + Add
          </Button>
        </div>
        <p>Fake items</p>
        <div className={styles.wrapperItems}>
          <div className={styles.items}>{fakeItems.map((e) => e)}</div>
          <Button
            type='dashed'
            className={styles.addButton}
            onClick={() => {
              setJ((p) => p + 1);
              setDisableBtn(false);
              setFakeItems((p) => [
                ...p,
                <CollectableInput
                  key={j.toString()}
                  identifier={`${j.toString()}-fake`}
                  deleteItem={deleteItem}
                  type={'fake'}
                  userEmail={session?.user.email}
                  enableSave={enableSave}
                />,
              ]);
            }}
          >
            + Add
          </Button>
        </div>
        <div className={styles.btnWrapper}>
          <Button
            htmlType='submit'
            type='primary'
            className={styles.btn}
            disabled={loadingUpdate || disableBtn}
          >
            {loadingUpdate ? (
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 30, color: '#fff' }}
                    spin
                  />
                }
              ></Spin>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default QuestSettings;
